import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowRight, ArrowLeft, Calendar, Wallet, Target, Sliders } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { authPost } from '../utils/api';

const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
];

const CATEGORIES = [
  'Food',
  'Transport',
  'Rent',
  'Entertainment',
  'Shopping',
  'Subscriptions',
  'Bills & Utilities',
  'Other'
];

const STEPS = [
  { icon: Wallet, title: 'Income', desc: 'Tell us about your income' },
  { icon: Calendar, title: 'Spending', desc: 'Your spending categories' },
  { icon: Target, title: 'Goals', desc: 'Set a savings goal' },
  { icon: Sliders, title: 'Style', desc: 'Budget preferences' },
];

export default function Onboarding() {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { updateUser } = useAuth();

  const [formData, setFormData] = useState({
    income_frequency: 'monthly',
    income_amount: '',
    pays_rent: false,
    rent_amount: 0,
    spending_categories: ['Food', 'Transport'],
    savings_goal_name: '',
    savings_goal_amount: '',
    savings_goal_date: '',
    budget_style: 'balanced',
    currency: 'USD'
  });

  const updateForm = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const toggleCategory = (cat) => {
    setFormData(prev => ({
      ...prev,
      spending_categories: prev.spending_categories.includes(cat)
        ? prev.spending_categories.filter(c => c !== cat)
        : [...prev.spending_categories, cat]
    }));
  };

  const handleNext = () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    setError('');
    setLoading(true);

    try {
      await authPost('/api/onboarding', {
        ...formData,
        income_amount: parseFloat(formData.income_amount) || 0,
        rent_amount: parseFloat(formData.rent_amount) || 0,
        savings_goal_amount: parseFloat(formData.savings_goal_amount) || 0,
      });
      updateUser({ onboarding_completed: true, currency: formData.currency });
      navigate('/dashboard');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save preferences');
    }
    setLoading(false);
  };

  const isStepValid = () => {
    switch (step) {
      case 0:
        return formData.income_amount && parseFloat(formData.income_amount) > 0;
      case 1:
        return formData.spending_categories.length > 0;
      case 2:
        return formData.savings_goal_name && formData.savings_goal_amount && formData.savings_goal_date;
      case 3:
        return true;
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F1A] flex flex-col px-4 py-8">
      {/* Background glow */}
      <div className="absolute top-0 left-1/3 w-96 h-96 bg-[#8B5CF6]/10 rounded-full blur-[100px]" />
      
      <div className="w-full max-w-lg mx-auto relative z-10 flex-1 flex flex-col">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                    i <= step ? 'gradient-ai' : 'bg-[#1F2937]'
                  }`}>
                    <Icon size={20} weight={i <= step ? 'duotone' : 'regular'} className="text-white" />
                  </div>
                  <span className={`text-xs mt-2 hidden sm:block ${i <= step ? 'text-[#F9FAFB]' : 'text-[#9CA3AF]'}`}>
                    {s.title}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="h-1 bg-[#1F2937] rounded-full overflow-hidden">
            <div 
              className="h-full gradient-ai transition-all duration-300"
              style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
            />
          </div>
        </div>

        <div className="card-default p-6 sm:p-8 animate-fade-in flex-1">
          <h2 className="text-2xl font-semibold mb-2">{STEPS[step].title}</h2>
          <p className="text-[#9CA3AF] mb-6">{STEPS[step].desc}</p>

          {error && (
            <div className="bg-[#EF4444]/10 border border-[#EF4444]/20 rounded-xl p-3 text-[#EF4444] text-sm mb-4">
              {error}
            </div>
          )}

          {/* Step 0: Income */}
          {step === 0 && (
            <div className="space-y-6">
              <div className="space-y-3">
                <Label className="text-[#F9FAFB]">Currency</Label>
                <Select value={formData.currency} onValueChange={(v) => updateForm('currency', v)}>
                  <SelectTrigger className="input-default" data-testid="currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111827] border-white/10">
                    {CURRENCIES.map(c => (
                      <SelectItem key={c.code} value={c.code} className="text-[#F9FAFB] focus:bg-[#1F2937]">
                        {c.symbol} {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label className="text-[#F9FAFB]">How often do you receive income?</Label>
                <RadioGroup 
                  value={formData.income_frequency} 
                  onValueChange={(v) => updateForm('income_frequency', v)}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="weekly" id="weekly" className="border-white/20 text-[#3B82F6]" />
                    <Label htmlFor="weekly" className="text-[#F9FAFB] cursor-pointer">Weekly</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="monthly" id="monthly" className="border-white/20 text-[#3B82F6]" />
                    <Label htmlFor="monthly" className="text-[#F9FAFB] cursor-pointer">Monthly</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label htmlFor="income" className="text-[#F9FAFB]">Average income amount</Label>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-medium text-[#9CA3AF] w-8">
                    {CURRENCIES.find(c => c.code === formData.currency)?.symbol}
                  </span>
                  <Input
                    id="income"
                    type="number"
                    value={formData.income_amount}
                    onChange={(e) => updateForm('income_amount', e.target.value)}
                    placeholder="0.00"
                    className="input-default flex-1"
                    data-testid="income-amount"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="rent"
                    checked={formData.pays_rent}
                    onCheckedChange={(c) => updateForm('pays_rent', c)}
                    className="border-white/20 data-[state=checked]:bg-[#3B82F6]"
                  />
                  <Label htmlFor="rent" className="text-[#F9FAFB] cursor-pointer">I pay rent</Label>
                </div>
                {formData.pays_rent && (
                  <div className="pl-6">
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-medium text-[#9CA3AF] w-8">
                        {CURRENCIES.find(c => c.code === formData.currency)?.symbol}
                      </span>
                      <Input
                        type="number"
                        value={formData.rent_amount}
                        onChange={(e) => updateForm('rent_amount', e.target.value)}
                        placeholder="0.00"
                        className="input-default flex-1"
                        data-testid="rent-amount"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 1: Categories */}
          {step === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-[#9CA3AF]">Select your main spending categories</p>
              <div className="grid grid-cols-2 gap-3">
                {CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => toggleCategory(cat)}
                    className={`p-3 rounded-xl text-sm text-left transition-all ${
                      formData.spending_categories.includes(cat)
                        ? 'bg-[#3B82F6]/20 border border-[#3B82F6]/40 text-[#F9FAFB]'
                        : 'bg-[#1F2937] border border-transparent text-[#9CA3AF] hover:border-white/10'
                    }`}
                    data-testid={`category-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Savings Goal */}
          {step === 2 && (
            <div className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="goalName" className="text-[#F9FAFB]">What are you saving for?</Label>
                <Input
                  id="goalName"
                  value={formData.savings_goal_name}
                  onChange={(e) => updateForm('savings_goal_name', e.target.value)}
                  placeholder="e.g., New Laptop, Emergency Fund"
                  className="input-default w-full"
                  data-testid="goal-name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="goalAmount" className="text-[#F9FAFB]">Target amount</Label>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-medium text-[#9CA3AF] w-8">
                    {CURRENCIES.find(c => c.code === formData.currency)?.symbol}
                  </span>
                  <Input
                    id="goalAmount"
                    type="number"
                    value={formData.savings_goal_amount}
                    onChange={(e) => updateForm('savings_goal_amount', e.target.value)}
                    placeholder="0.00"
                    className="input-default flex-1"
                    data-testid="goal-amount"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="goalDate" className="text-[#F9FAFB]">Target date</Label>
                <Input
                  id="goalDate"
                  type="date"
                  value={formData.savings_goal_date}
                  onChange={(e) => updateForm('savings_goal_date', e.target.value)}
                  className="input-default w-full"
                  min={new Date().toISOString().split('T')[0]}
                  data-testid="goal-date"
                />
              </div>
            </div>
          )}

          {/* Step 3: Budget Style */}
          {step === 3 && (
            <div className="space-y-4">
              <RadioGroup 
                value={formData.budget_style} 
                onValueChange={(v) => updateForm('budget_style', v)}
                className="space-y-3"
              >
                <label className={`flex items-start gap-4 p-4 rounded-xl cursor-pointer transition-all ${
                  formData.budget_style === 'strict' 
                    ? 'bg-[#3B82F6]/20 border border-[#3B82F6]/40' 
                    : 'bg-[#1F2937] border border-transparent hover:border-white/10'
                }`}>
                  <RadioGroupItem value="strict" className="mt-1 border-white/20 text-[#3B82F6]" />
                  <div>
                    <p className="font-medium text-[#F9FAFB]">Strict</p>
                    <p className="text-sm text-[#9CA3AF]">I want firm limits and alerts when I overspend</p>
                  </div>
                </label>
                <label className={`flex items-start gap-4 p-4 rounded-xl cursor-pointer transition-all ${
                  formData.budget_style === 'balanced' 
                    ? 'bg-[#3B82F6]/20 border border-[#3B82F6]/40' 
                    : 'bg-[#1F2937] border border-transparent hover:border-white/10'
                }`}>
                  <RadioGroupItem value="balanced" className="mt-1 border-white/20 text-[#3B82F6]" />
                  <div>
                    <p className="font-medium text-[#F9FAFB]">Balanced</p>
                    <p className="text-sm text-[#9CA3AF]">Flexible budgets with helpful suggestions</p>
                  </div>
                </label>
                <label className={`flex items-start gap-4 p-4 rounded-xl cursor-pointer transition-all ${
                  formData.budget_style === 'flexible' 
                    ? 'bg-[#3B82F6]/20 border border-[#3B82F6]/40' 
                    : 'bg-[#1F2937] border border-transparent hover:border-white/10'
                }`}>
                  <RadioGroupItem value="flexible" className="mt-1 border-white/20 text-[#3B82F6]" />
                  <div>
                    <p className="font-medium text-[#F9FAFB]">Flexible</p>
                    <p className="text-sm text-[#9CA3AF]">Just track spending, no strict limits</p>
                  </div>
                </label>
              </RadioGroup>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex gap-4 mt-6">
          {step > 0 && (
            <Button
              type="button"
              onClick={handleBack}
              className="btn-secondary flex-1"
              data-testid="onboarding-back"
            >
              <ArrowLeft size={20} className="mr-2" />
              Back
            </Button>
          )}
          
          {step < STEPS.length - 1 ? (
            <Button
              type="button"
              onClick={handleNext}
              disabled={!isStepValid()}
              className="btn-primary flex-1"
              data-testid="onboarding-next"
            >
              Next
              <ArrowRight size={20} className="ml-2" />
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="btn-ai flex-1"
              data-testid="onboarding-finish"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Start Using UniWallet
                  <ArrowRight size={20} className="ml-2" />
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
